/**
 * Cooling system list row — web: table columns + status bar; native: card (#996 Phase 2).
 * Mirrors SmallUpsTableRow.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  Dimensions,
} from 'react-native';

import {
  formatCoolingSystemTableCell,
  getCoolingSystemColumnSubline,
  getCoolingSystemDisplayName,
  coolingSystemColumnUsesDoubleLine,
  coolingSystemTableColumnsForLayout,
  coolingSystemTableHeaderLabel,
  COOLING_SYSTEM_TABLE_COLUMN_ALIGN,
  type CoolingSystemTableColumnKey,
  type CoolingSystemTableLayout,
} from './coolingSystemListUtils';
import { coolingSystemColumnCellStyle } from './coolingSystemTableColumnLayout';
import {
  COOLING_SYSTEM_ROW_ACTION_DELETE_LABEL,
  COOLING_SYSTEM_ROW_ACTION_DUPLICATE_LABEL,
  COOLING_SYSTEM_ROW_ACTION_EDIT_LABEL,
  COOLING_SYSTEM_ROW_ACTION_VIEW_LABEL,
} from '../../constants/coolingSystemConfigurationsSubsectionCopy';
import { colors, spacing } from '../../theme';
import { isNativeCompactLayout } from '../../utils/locationTagListDisplay';
import { MoreHorizontal, Copy, Trash2, GripVertical, Edit } from '../icons';
import { LocationRoomCell } from '../locationTags/LocationRoomCell';
import { useTableRowDragAndDrop } from '../recordTable';
import { NativeCompactNameCell } from '../recordTable/NativeCompactNameCell';
import { RecordTableCellSubline } from '../recordTable/RecordTableCellSubline';
import {
  repeatableCompactActionsColumnReserved,
  repeatableRowDragState,
  repeatableRowMenuState,
} from '../recordTable/repeatableRowChrome';
import { RepeatableUnitStatusPill } from '../recordTable/RepeatableUnitStatusPill';

import type { CoolingSystemConfiguration } from '../../types/coolingSystem';

const COOLING_SYSTEM_ROW_MENU_MIN_WIDTH = 180;
/** Approx height of one menu row; used to flip the native dropdown up near the screen bottom. */
const COOLING_SYSTEM_ROW_MENU_ITEM_HEIGHT = 44;
const COOLING_SYSTEM_ROW_MENU_PORTAL_ATTR =
  'data-cooling-system-row-menu-portal';
const COOLING_SYSTEM_ROW_MENU_BUTTON_ATTR =
  'data-cooling-system-row-menu-button';

const scheduleMenuPositionUpdate = (update: () => void) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(update);
    return;
  }
  setTimeout(update, 0);
};

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

interface CoolingSystemTableRowProps {
  unit: CoolingSystemConfiguration;
  onPress?: (unit: CoolingSystemConfiguration) => void;
  /** Opens editor from overflow menu. Defaults to {@link onPress} when omitted. */
  onEdit?: (unit: CoolingSystemConfiguration) => void;
  onDuplicate?: (unit: CoolingSystemConfiguration) => void;
  onDelete?: (unit: CoolingSystemConfiguration) => void;
  disabled?: boolean;
  /** View-only list: row opens details; edit/duplicate/delete menu hidden. */
  readOnly?: boolean;
  isPlaceholder?: boolean;
  /** Web table card: omit bottom divider on last row. */
  isLastInTable?: boolean;
  testID?: string;
  /** Web drag reorder */
  reorderEnabled?: boolean;
  rowIndex?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragHandleStart?: (rowIndex: number) => void;
  onDragHandleMove?: (pageY: number) => void;
  onDragHandleEnd?: () => void;
  onRowDragOver?: (rowIndex: number) => void;
  onRowDrop?: (rowIndex: number) => void;
  onRowLayout?: (
    rowIndex: number,
    bounds: { top: number; bottom: number },
  ) => void;
  /** Desktop web table vs compact mobile/narrow. */
  tableLayout?: CoolingSystemTableLayout;
  /** Browse-by-location: omit redundant location column. */
  hideLocationColumn?: boolean;
  /** Devices view: STATUS column with EXISTING/NEW pills. */
  showStatusColumn?: boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
}

interface CoolingSystemRowOverflowMenuProps {
  testID?: string;
  actionsLabel: string;
  menuAvailable: boolean;
  canEdit: boolean;
  openActionLabel: string;
  canDuplicate: boolean;
  canDelete: boolean;
  disabled: boolean;
  useWebDropdown: boolean;
  stopRowActivation?: (event?: { stopPropagation?: () => void }) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const CoolingSystemRowMenuItem: React.FC<{
  testID?: string;
  onPress: () => void;
  accessibilityLabel: string;
  destructive?: boolean;
  children: React.ReactNode;
}> = ({
  testID,
  onPress,
  accessibilityLabel,
  destructive = false,
  children,
}) => {
  const handlePress = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    onPress();
  };

  if (Platform.OS === 'web') {
    return (
      <TouchableOpacity
        testID={testID}
        style={styles.menuItem}
        onPress={handlePress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        {children}
      </TouchableOpacity>
    );
  }

  return (
    <Pressable
      testID={testID}
      style={styles.menuItem}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      {children}
    </Pressable>
  );
};

const WEB_MENU_ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: 'inherit',
  textAlign: 'left',
};

const CoolingSystemRowOverflowMenu: React.FC<
  CoolingSystemRowOverflowMenuProps
> = ({
  testID,
  actionsLabel,
  menuAvailable,
  canEdit,
  openActionLabel,
  canDuplicate,
  canDelete,
  disabled,
  useWebDropdown,
  stopRowActivation,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const hasMenu = menuAvailable;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuPositioned, setMenuPositioned] = useState(false);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuPositioned(false);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!useWebDropdown || typeof window === 'undefined') {
      return;
    }
    const anchor = menuAnchorRef.current;
    if (!anchor?.measureInWindow) {
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      setMenuPosition({
        top: y + height + 4,
        left: x + width - COOLING_SYSTEM_ROW_MENU_MIN_WIDTH,
      });
    });
  }, [useWebDropdown]);

  const runMenuAction = useCallback(
    (action: () => void) => {
      stopRowActivation?.();
      closeMenu();
      action();
    },
    [closeMenu, stopRowActivation],
  );

  const openNativeMenu = useCallback(() => {
    setMenuPositioned(false);
    setMenuOpen(true);
    const anchor = menuAnchorRef.current;
    if (!anchor?.measureInWindow) {
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      const screen = Dimensions.get('window');
      const itemCount = [canEdit, canDuplicate, canDelete].filter(
        Boolean,
      ).length;
      const dropdownHeight =
        itemCount * COOLING_SYSTEM_ROW_MENU_ITEM_HEIGHT + 8;
      const edge = 8;
      let top = y + height + 4;
      let left = x + width - COOLING_SYSTEM_ROW_MENU_MIN_WIDTH;
      if (top + dropdownHeight > screen.height - edge) {
        top = y - dropdownHeight - 4;
      }
      if (left + COOLING_SYSTEM_ROW_MENU_MIN_WIDTH > screen.width - edge) {
        left = screen.width - COOLING_SYSTEM_ROW_MENU_MIN_WIDTH - edge;
      }
      top = Math.max(edge, top);
      left = Math.max(edge, left);
      setMenuPosition({ top, left });
      setMenuPositioned(true);
    });
  }, [canEdit, canDuplicate, canDelete]);

  const handleMenuPress = (event?: { stopPropagation?: () => void }) => {
    stopRowActivation?.(event);
    if (!hasMenu || disabled) {
      return;
    }
    if (menuOpen) {
      closeMenu();
      return;
    }
    if (useWebDropdown) {
      setMenuOpen(true);
      scheduleMenuPositionUpdate(() => {
        updateMenuPosition();
      });
    } else {
      openNativeMenu();
    }
  };

  useEffect(() => {
    if (!menuOpen || !useWebDropdown) {
      return undefined;
    }
    updateMenuPosition();
    if (
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return undefined;
    }
    const handleReposition = () => {
      updateMenuPosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen, useWebDropdown, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const portalSelector = testID
        ? `[data-cooling-system-row-menu-portal="${testID}"]`
        : `[${COOLING_SYSTEM_ROW_MENU_PORTAL_ATTR}]`;
      const buttonSelector = testID
        ? `[data-cooling-system-row-menu-button="${testID}"]`
        : `[${COOLING_SYSTEM_ROW_MENU_BUTTON_ATTR}]`;
      const portal = document.querySelector(portalSelector);
      if (portal?.contains(target)) {
        return;
      }
      const buttonEl = document.querySelector(buttonSelector);
      if (buttonEl?.contains(target)) {
        return;
      }
      closeMenu();
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, closeMenu, testID]);

  const nativeMenuDropdownContent = menuOpen ? (
    <>
      {canEdit ? (
        <CoolingSystemRowMenuItem
          testID={testID ? `${testID}-menu-edit` : undefined}
          onPress={() => runMenuAction(onEdit)}
          accessibilityLabel={openActionLabel}
        >
          <Edit size={16} color={colors.textPrimary} />
          <Text style={styles.menuItemText}>{openActionLabel}</Text>
        </CoolingSystemRowMenuItem>
      ) : null}
      {canDuplicate ? (
        <CoolingSystemRowMenuItem
          testID={testID ? `${testID}-menu-duplicate` : undefined}
          onPress={() => runMenuAction(onDuplicate)}
          accessibilityLabel={COOLING_SYSTEM_ROW_ACTION_DUPLICATE_LABEL}
        >
          <Copy size={16} color={colors.textPrimary} />
          <Text style={styles.menuItemText}>
            {COOLING_SYSTEM_ROW_ACTION_DUPLICATE_LABEL}
          </Text>
        </CoolingSystemRowMenuItem>
      ) : null}
      {canDelete ? (
        <CoolingSystemRowMenuItem
          testID={testID ? `${testID}-menu-delete` : undefined}
          onPress={() => runMenuAction(onDelete)}
          accessibilityLabel={COOLING_SYSTEM_ROW_ACTION_DELETE_LABEL}
          destructive
        >
          <Trash2 size={16} color={colors.error} />
          <Text style={[styles.menuItemText, styles.menuItemTextDestructive]}>
            {COOLING_SYSTEM_ROW_ACTION_DELETE_LABEL}
          </Text>
        </CoolingSystemRowMenuItem>
      ) : null}
    </>
  ) : null;

  const webPortaledMenu =
    useWebDropdown && menuOpen && typeof document !== 'undefined'
      ? (() => {
          const createPortal = getCreatePortal();
          if (!createPortal) {
            return null;
          }
          return createPortal(
            <div
              data-cooling-system-row-menu-portal={testID ?? 'anonymous'}
              data-testid={testID ? `${testID}-menu-dropdown` : undefined}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                minWidth: COOLING_SYSTEM_ROW_MENU_MIN_WIDTH,
                zIndex: 999999,
                backgroundColor: colors.backgroundPrimary,
                border: `1px solid ${colors.borderCard}`,
                borderRadius: 8,
                paddingTop: 4,
                paddingBottom: 4,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {canEdit ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-edit` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.textPrimary,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onEdit);
                  }}
                >
                  {openActionLabel}
                </button>
              ) : null}
              {canDuplicate ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-duplicate` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.textPrimary,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onDuplicate);
                  }}
                >
                  {COOLING_SYSTEM_ROW_ACTION_DUPLICATE_LABEL}
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-delete` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.error,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onDelete);
                  }}
                >
                  {COOLING_SYSTEM_ROW_ACTION_DELETE_LABEL}
                </button>
              ) : null}
            </div>,
            document.body,
          );
        })()
      : null;

  if (!hasMenu) {
    return (
      <View style={styles.menuAnchor}>
        <Pressable
          testID={testID ? `${testID}-menu` : undefined}
          style={[styles.menuButton, styles.menuButtonDisabled]}
          disabled
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={`More actions for ${actionsLabel}`}
          accessibilityState={{ disabled: true }}
        >
          <MoreHorizontal size={18} color={colors.textMuted} />
        </Pressable>
      </View>
    );
  }

  return (
    <View
      ref={menuAnchorRef}
      style={styles.menuAnchor}
      {...(useWebDropdown && testID
        ? ({ 'data-cooling-system-row-menu-button': testID } as Record<
            string,
            string
          >)
        : {})}
    >
      <Pressable
        testID={testID ? `${testID}-menu` : undefined}
        style={[styles.menuButton, disabled && styles.menuButtonDisabled]}
        onPress={handleMenuPress}
        onPressIn={stopRowActivation}
        disabled={disabled}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={`More actions for ${actionsLabel}`}
        accessibilityHint="Opens cooling system actions menu"
        accessibilityState={{ expanded: menuOpen, disabled }}
      >
        <MoreHorizontal size={18} color={colors.textSecondary} />
      </Pressable>
      {webPortaledMenu}
      {!webPortaledMenu ? (
        <Modal
          visible={menuOpen}
          transparent
          animationType="fade"
          onRequestClose={closeMenu}
          statusBarTranslucent
        >
          <Pressable style={styles.menuModalBackdrop} onPress={closeMenu}>
            <View
              onStartShouldSetResponder={() => true}
              style={[
                styles.menuDropdownModal,
                menuPositioned
                  ? { top: menuPosition.top, left: menuPosition.left }
                  : styles.menuDropdownModalHidden,
              ]}
              testID={testID ? `${testID}-menu-dropdown` : undefined}
            >
              {nativeMenuDropdownContent}
            </View>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
};

function coolingSystemColumnAlign(
  column: CoolingSystemTableColumnKey,
): 'left' | 'center' {
  return (
    COOLING_SYSTEM_TABLE_COLUMN_ALIGN[
      column as keyof typeof COOLING_SYSTEM_TABLE_COLUMN_ALIGN
    ] ?? 'left'
  );
}

const WEB_ACTIONS_COLUMN_WIDTH = 44;

// --- Table row (desktop web + compact mobile/narrow) ---

const CoolingSystemTableRowTable: React.FC<CoolingSystemTableRowProps> = ({
  unit,
  onPress,
  onEdit,
  onDuplicate,
  onDelete,
  disabled = false,
  readOnly = false,
  isPlaceholder = false,
  isLastInTable = false,
  testID,
  reorderEnabled = false,
  rowIndex,
  isDragging = false,
  isDragOver = false,
  onDragHandleStart,
  onDragHandleMove,
  onDragHandleEnd,
  onRowDragOver,
  onRowDrop,
  onRowLayout,
  registerRowRemeasure,
  tableLayout = 'desktop',
  hideLocationColumn = false,
  showStatusColumn = false,
  resolveLocationTagName,
}) => {
  const isCompact = tableLayout === 'compact';
  const isNativeCompact = isNativeCompactLayout(tableLayout);
  const columns = coolingSystemTableColumnsForLayout(tableLayout, {
    hideLocationColumn,
    showStatusColumn,
  });
  const useWebDropdown = Platform.OS === 'web';
  const isExisting = unit.status === 'existing';
  const statusLabel = isExisting ? 'Existing' : 'New';
  const isRowNavigable = Boolean(onPress) && !disabled;
  const isInteractive = isRowNavigable;
  const openEditor = onEdit ?? onPress;
  const canEdit =
    Boolean(openEditor) &&
    !disabled &&
    (!readOnly || showStatusColumn) &&
    !isPlaceholder;
  const canDuplicate =
    Boolean(onDuplicate) && !disabled && !readOnly && !isPlaceholder;
  const canDelete =
    Boolean(onDelete) && !disabled && !readOnly && !isPlaceholder;
  const hasMenuActions =
    Boolean(openEditor) || Boolean(onDuplicate) || Boolean(onDelete);
  const { showOverflowMenu, menuDisabled } = repeatableRowMenuState({
    disabled,
    readOnly,
    isPlaceholder,
    canEdit,
    canDuplicate,
    canDelete,
  });

  const openRow = () => {
    if (isInteractive) {
      onPress?.(unit);
    }
  };

  const stopRowActivation = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      event?.stopPropagation?.();
    },
    [],
  );

  const { showDragHandle, dragHandleActive } = repeatableRowDragState({
    reorderEnabled,
    disabled,
    readOnly,
    isPlaceholder,
    rowIndex,
  });
  const dragHandleRef = useRef<View>(null);
  const dropRowRef = useRef<View>(null);

  const { nativeDragHandleProps, onDropRowLayout } = useTableRowDragAndDrop({
    enabled: dragHandleActive,
    rowIndex: rowIndex ?? 0,
    dragHandleRef,
    dropRowRef,
    onDragHandleStart,
    onDragHandleMove,
    onDragHandleEnd,
    onRowDragOver,
    onRowDrop,
    onRowLayout,
    registerRowRemeasure,
  });

  const statusBarCell = (
    <View
      style={[
        styles.statusBar,
        styles.statusBarEmbedded,
        isExisting ? styles.statusBarExisting : styles.statusBarNew,
      ]}
      accessibilityElementsHidden
    />
  );

  const dragHandleCell = showDragHandle ? (
    <View
      ref={dragHandleRef}
      testID={testID ? `${testID}-drag-handle` : undefined}
      style={[
        styles.dragHandleCell,
        !dragHandleActive && styles.dragHandleDisabled,
      ]}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      pointerEvents="box-only"
      accessibilityRole="button"
      accessibilityLabel="Drag to reorder cooling system"
      accessibilityState={{ disabled: !dragHandleActive }}
      {...nativeDragHandleProps}
    >
      <GripVertical size={16} color={colors.textMuted} />
    </View>
  ) : null;

  const rowMainCells = (
    <>
      {columns.map(column => {
        const displayValue = isPlaceholder
          ? '—'
          : formatCoolingSystemTableCell(unit, column);
        const isEmptyPlaceholder = isPlaceholder;
        const roomTagName =
          column === 'ROOM' && !isPlaceholder
            ? resolveLocationTagName?.(unit.locationTagId ?? null)
            : null;
        const subline = !isPlaceholder
          ? getCoolingSystemColumnSubline(column, unit, tableLayout)
          : undefined;
        const showDoubleLine =
          coolingSystemColumnUsesDoubleLine(column, tableLayout) &&
          Boolean(subline);

        const columnAlign = coolingSystemColumnAlign(column);
        const isCentered = columnAlign === 'center';

        return (
          <View
            key={column}
            style={[
              isCompact ? styles.compactTableCell : styles.tableCell,
              isCentered ? styles.tableCellCenter : styles.tableCellStart,
              coolingSystemColumnCellStyle(column, tableLayout),
            ]}
          >
            {column === 'STATUS' ? (
              <RepeatableUnitStatusPill
                status={unit.status}
                testID={testID ? `${testID}-status` : undefined}
              />
            ) : column === 'ROOM' ? (
              <LocationRoomCell
                locationTagName={roomTagName}
                compact={isCompact}
              />
            ) : isNativeCompact && column === 'NAME' ? (
              // Option B (#999): NAME stacks the folded location tag (step view)
              // and/or the STATUS pill (Devices view) beneath the primary text
              // instead of standalone ROOM / STATUS columns. Shared cell keeps
              // all six device tables identical.
              <NativeCompactNameCell
                displayValue={displayValue}
                isEmptyPlaceholder={isPlaceholder}
                locationTagName={
                  hideLocationColumn
                    ? null
                    : resolveLocationTagName?.(unit.locationTagId ?? null)
                }
                showStatus={showStatusColumn}
                status={unit.status}
                testID={testID}
              />
            ) : showDoubleLine ? (
              <>
                <Text
                  style={[
                    styles.modelPrimaryText,
                    isCompact && styles.compactPrimaryText,
                    isPlaceholder && styles.placeholderText,
                  ]}
                  numberOfLines={1}
                >
                  {displayValue}
                </Text>
                <RecordTableCellSubline
                  column={column}
                  subline={subline}
                  textStyle={[
                    styles.modelSublineText,
                    isCompact && styles.compactSublineText,
                  ]}
                  compact={isCompact}
                />
              </>
            ) : (
              <Text
                style={[
                  styles.tableCellText,
                  isCompact && styles.compactCellText,
                  isCentered && styles.tableCellTextCenter,
                  isEmptyPlaceholder && styles.placeholderMuted,
                ]}
                numberOfLines={isCompact && !isCentered ? 2 : 1}
              >
                {displayValue}
              </Text>
            )}
          </View>
        );
      })}
    </>
  );

  const showCompactActionsColumn = repeatableCompactActionsColumnReserved({
    isCompact,
    readOnly,
    hasOpenAction: Boolean(openEditor) && (!readOnly || showStatusColumn),
  });
  const rowActionsCell = (
    <View
      style={[
        styles.tableActionsCell,
        isCompact &&
          (showCompactActionsColumn
            ? styles.tableActionsCellCompact
            : styles.tableActionsCellHidden),
      ]}
    >
      {showOverflowMenu ? (
        <CoolingSystemRowOverflowMenu
          testID={testID}
          actionsLabel={
            getCoolingSystemDisplayName(unit) ??
            formatCoolingSystemTableCell(unit, 'NAME')
          }
          menuAvailable={hasMenuActions}
          canEdit={canEdit}
          openActionLabel={
            readOnly
              ? COOLING_SYSTEM_ROW_ACTION_VIEW_LABEL
              : COOLING_SYSTEM_ROW_ACTION_EDIT_LABEL
          }
          canDuplicate={canDuplicate}
          canDelete={canDelete}
          disabled={menuDisabled}
          useWebDropdown={useWebDropdown}
          stopRowActivation={stopRowActivation}
          onEdit={() => openEditor?.(unit)}
          onDuplicate={() => onDuplicate?.(unit)}
          onDelete={() => onDelete?.(unit)}
        />
      ) : null}
    </View>
  );

  const a11yLabel = isPlaceholder
    ? 'No cooling system details yet'
    : `${
        getCoolingSystemDisplayName(unit) ??
        formatCoolingSystemTableCell(unit, 'NAME')
      }, ${statusLabel} cooling system`;

  if (!isInteractive) {
    return (
      <View
        ref={dropRowRef}
        onLayout={onDropRowLayout}
        testID={testID || `cooling-system-row-${unit.id ?? 'placeholder'}`}
        style={[
          styles.tableRow,
          styles.tableRowEmbedded,
          isCompact && styles.tableRowCompact,
          isLastInTable && styles.tableRowEmbeddedLast,
          styles.tableRowStatic,
          disabled && styles.tableRowDisabled,
          isDragOver && styles.tableRowDragOver,
        ]}
        accessibilityRole="text"
        accessibilityLabel={a11yLabel}
      >
        {statusBarCell}
        {dragHandleCell}
        {rowMainCells}
        {rowActionsCell}
      </View>
    );
  }

  const rowShellStyle = [
    styles.tableRow,
    styles.tableRowEmbedded,
    isCompact && styles.tableRowCompact,
    isLastInTable && styles.tableRowEmbeddedLast,
    disabled && styles.tableRowDisabled,
    isDragging && styles.tableRowDragging,
    isDragOver && styles.tableRowDragOver,
  ];

  return (
    <View ref={dropRowRef} style={rowShellStyle} onLayout={onDropRowLayout}>
      {statusBarCell}
      {dragHandleCell}
      <Pressable
        testID={testID || `cooling-system-row-${unit.id}`}
        style={({ pressed }) => [
          styles.tableRowMainPressable,
          pressed && styles.tableRowPressed,
        ]}
        onPress={openRow}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        accessibilityHint={
          readOnly
            ? 'Tap to view cooling system details'
            : 'Tap to edit cooling system'
        }
      >
        {rowMainCells}
      </Pressable>
      {rowActionsCell}
    </View>
  );
};

export const CoolingSystemTableHeader: React.FC<{
  reorderEnabled?: boolean;
  showDragColumn?: boolean;
  tableLayout?: CoolingSystemTableLayout;
  hideLocationColumn?: boolean;
  showStatusColumn?: boolean;
  readOnly?: boolean;
}> = ({
  reorderEnabled = false,
  showDragColumn = false,
  tableLayout = 'desktop',
  hideLocationColumn = false,
  showStatusColumn = false,
  readOnly = false,
}) => {
  const isCompact = tableLayout === 'compact';
  const showCompactActionsColumn = repeatableCompactActionsColumnReserved({
    isCompact,
    readOnly,
    hasOpenAction: readOnly && showStatusColumn,
  });
  const columns = coolingSystemTableColumnsForLayout(tableLayout, {
    hideLocationColumn,
    showStatusColumn,
  });
  const showDragSpacer = reorderEnabled || showDragColumn;

  return (
    <View
      style={[styles.headerRow, isCompact && styles.headerRowCompact]}
      accessibilityElementsHidden
    >
      <View style={styles.headerBarSpacer} />
      {showDragSpacer ? <View style={styles.headerDragSpacer} /> : null}
      {columns.map(column => {
        const isCentered = coolingSystemColumnAlign(column) === 'center';
        return (
          <View
            key={column}
            style={[
              isCompact ? styles.compactHeaderCell : styles.headerCell,
              isCentered ? styles.headerCellCenter : styles.headerCellStart,
              coolingSystemColumnCellStyle(column, tableLayout),
            ]}
          >
            <Text
              style={[
                styles.headerText,
                isCompact && styles.headerTextCompact,
                isCentered && styles.headerTextCenter,
              ]}
              numberOfLines={isCompact ? 1 : undefined}
            >
              {coolingSystemTableHeaderLabel(column, tableLayout)}
            </Text>
          </View>
        );
      })}
      <View
        style={[
          styles.headerActionsSpacer,
          isCompact &&
            (showCompactActionsColumn
              ? styles.headerActionsSpacerCompact
              : styles.headerActionsSpacerHidden),
        ]}
      />
    </View>
  );
};

export const CoolingSystemTableRow: React.FC<
  CoolingSystemTableRowProps
> = props => <CoolingSystemTableRowTable {...props} />;

const styles = StyleSheet.create({
  tableRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    minHeight: 60,
    overflow: 'visible',
    marginBottom: 4,
    cursor: 'pointer' as unknown as undefined,
  },
  tableRowStatic: {
    cursor: 'default' as unknown as undefined,
  },
  tableRowDisabled: {
    opacity: 0.6,
  },
  tableRowDragging: {
    opacity: 0.45,
  },
  tableRowDragOver: {
    backgroundColor: colors.surfaceAccentMuted,
  },
  dragHandleCell: {
    width: 28,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab' as unknown as undefined,
  },
  dragHandleDisabled: {
    opacity: 0.4,
    cursor: 'default' as unknown as undefined,
  },
  headerDragSpacer: {
    width: 28,
    flexShrink: 0,
  },
  tableRowPressed: {
    opacity: 0.9,
  },
  statusBar: {
    width: 5,
    flexShrink: 0,
  },
  statusBarEmbedded: {
    borderRadius: 0,
  },
  statusBarExisting: {
    backgroundColor: colors.success,
  },
  statusBarNew: {
    backgroundColor: colors.primary,
  },
  tableCell: {
    paddingVertical: 14,
    minWidth: 0,
    gap: 2,
  },
  compactTableCell: {
    paddingVertical: 10,
    minWidth: 0,
    gap: 1,
  },
  compactPrimaryText: {
    fontSize: 12,
  },
  compactSublineText: {
    fontSize: 11,
  },
  compactCellText: {
    fontSize: 12,
  },
  tableCellStart: {
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableCellText: {
    fontSize: 13,
    fontWeight: '400',
    color: colors.textPrimary,
  },
  tableCellTextCenter: {
    textAlign: 'center',
    width: '100%',
  },
  modelPrimaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  modelSublineText: {
    fontSize: 12,
    fontWeight: '400',
    color: colors.textSecondary,
  },
  placeholderText: {
    fontWeight: '400',
    fontStyle: 'italic',
    color: colors.textMuted,
  },
  placeholderMuted: {
    color: colors.textMuted,
  },
  tableActionsCell: {
    width: WEB_ACTIONS_COLUMN_WIDTH,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  tableActionsCellCompact: {
    width: 36,
  },
  tableActionsCellHidden: {
    width: 0,
    display: 'none' as unknown as undefined,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
    backgroundColor: colors.backgroundMuted,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    ...Platform.select({
      web: {
        width: '100%',
        alignSelf: 'stretch',
      },
    }),
  },
  headerRowCompact: {
    minHeight: 34,
  },
  tableRowEmbedded: {
    borderWidth: 0,
    borderRadius: 0,
    marginBottom: 0,
    minHeight: 56,
    backgroundColor: colors.backgroundPrimary,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    ...Platform.select({
      web: {
        width: '100%',
        alignSelf: 'stretch',
      },
    }),
  },
  tableRowCompact: {
    minHeight: 52,
  },
  tableRowEmbeddedLast: {
    borderBottomWidth: 0,
  },
  tableRowMainPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    minWidth: 0,
    cursor: 'pointer' as unknown as undefined,
  },
  headerBarSpacer: {
    width: 5,
    flexShrink: 0,
  },
  headerCell: {
    minWidth: 0,
    justifyContent: 'center',
  },
  compactHeaderCell: {
    minWidth: 0,
    justifyContent: 'center',
  },
  headerCellStart: {
    alignItems: 'flex-start',
  },
  headerCellCenter: {
    alignItems: 'center',
  },
  headerText: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  headerTextCompact: {
    fontSize: 9,
    letterSpacing: 0.35,
  },
  headerTextCenter: {
    textAlign: 'center',
    width: '100%',
  },
  headerActionsSpacer: {
    width: WEB_ACTIONS_COLUMN_WIDTH,
    flexShrink: 0,
  },
  headerActionsSpacerCompact: {
    width: 36,
  },
  headerActionsSpacerHidden: {
    width: 0,
    display: 'none' as unknown as undefined,
  },
  menuAnchor: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 20,
  },
  menuButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
      },
    }),
  },
  menuButtonDisabled: {
    opacity: 0.45,
    ...Platform.select({
      web: {
        cursor: 'default' as unknown as undefined,
      },
    }),
  },
  menuModalBackdrop: {
    flex: 1,
  },
  menuDropdownModal: {
    position: 'absolute',
    minWidth: COOLING_SYSTEM_ROW_MENU_MIN_WIDTH,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
  },
  menuDropdownModalHidden: {
    opacity: 0,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
      },
    }),
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textPrimary,
  },
  menuItemTextDestructive: {
    color: colors.error,
  },
  menuButtonPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.35,
  },
});
